import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import Loader from 'components/partials/loader/loader';
import { renderGoal } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-cell/goals-matrix-cell';
import { formatDate } from 'helpers/dates-formatter';

import { getClientGoalRevisions } from 'api/generated/client-goal-revisions/client-goal-revisions';

import type { Goal } from 'containers/admin/clients/client/tabs-components/goals-tab/types';
import type { ClientGoalRevisionDto } from 'api/generated/schemas';

const ActionTypes: Record<string, string> = {
  INSERT: 'Goal Created',
  UPDATE: 'Goal Updated',
};

interface HistoryProps {
  data: { id: string | number; category: string };
  userId: string | undefined;
  clientId: string | number | undefined;
}

const History = ({
  data: { id, category },
  userId,
  clientId,
}: HistoryProps): React.JSX.Element => {
  const [history, setHistory] = useState<ClientGoalRevisionDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [visibleItems, setVisibleItems] = useState<number>(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    const fetchGoalHistory = async (): Promise<void> => {
      if (!userId || !clientId || !id) {
        setLoading(false);

        return;
      }

      try {
        const historyData = await getClientGoalRevisions(
          userId,
          Number(clientId),
          Number(id)
        );

        setHistory(historyData);

        historyData.forEach((_, index) => {
          const timer = setTimeout(() => {
            setVisibleItems((prev) => prev + 1);
          }, index * 300);

          timers.push(timer);
        });
      } catch (error) {
        console.error('Error while fetching goal history', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoalHistory();

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [userId, clientId, id]);

  const renderRevisionMetadata = (
    createdBy: string | undefined,
    createdDate: string | null
  ): React.JSX.Element => (
    <div className="text-navy-500 text-xs">
      <p data-testid="history-date">{createdDate}</p>
      <p data-testid="history-email">{createdBy}</p>
    </div>
  );

  const renderContent = (revision: Goal): React.JSX.Element => {
    const renderOtherGoalContent = (
      label: string,
      value: React.ReactNode
    ): React.JSX.Element => (
      <div className="flex gap-1 items-baseline">
        <span className="text-gray-400 shrink-0">{label}</span>
        <span className="text-gray-600 font-normal"> {value}</span>
      </div>
    );

    if (category === 'Other') {
      return (
        <div className="font-semibold font-sm">
          {renderOtherGoalContent(
            'Title:',
            (revision as { name?: string }).name
          )}
          {renderOtherGoalContent('Description:', revision.description)}
        </div>
      );
    }

    return renderGoal(revision);
  };

  const renderAction = (type: string): React.JSX.Element => (
    <p className="font-bold text-navy-800">{ActionTypes[type]}</p>
  );

  const renderGoalVersion = (
    type: string,
    item: Goal | undefined,
    summary: string | undefined
  ): React.JSX.Element => (
    <div
      className={classNames('flex gap-1 items-baseline text-gray-600', {
        'flex-col gap-0': category === 'Other',
      })}
    >
      <p className="text-gray-700 shrink-0 font-sm">{`${type} Version:`}</p>
      {renderContent(item as Goal)}
      {summary && (
        <span className="font-normal text-gray-600 text-sm">{`, ${summary}`}</span>
      )}
    </div>
  );

  const renderHistoryItem = (
    item: ClientGoalRevisionDto,
    index: number
  ): React.JSX.Element => {
    const { createdBy, createdDate, type } = item.metadata ?? {};
    const summary = item.revision?.summary;
    const formattedDate = formatDate(createdDate ?? null, 'MMM D, YY h:mm A');

    return (
      <>
        {renderAction(type ?? '')}
        {renderRevisionMetadata(createdBy, formattedDate)}
        <div className="mt-2 flex font-semibold gap-1 flex-col">
          {type === 'UPDATE' &&
            renderGoalVersion(
              'Prior',
              history[index + 1]?.revision as Goal | undefined,
              history[index + 1]?.revision?.summary
            )}
          {renderGoalVersion(
            type === 'INSERT' ? 'Initial' : 'New',
            item.revision as Goal | undefined,
            summary
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <Loader
        loading={true}
        overlay="white"
        customClass="opacity-75"
        fullScreen={false}
      />
    );
  }

  return (
    <div
      className="relative flex flex-col items-start h-full"
      data-testid="goal-history-tab"
    >
      {history.map((item, index) => (
        <div
          key={item.metadata?.createdDate ?? index}
          className={classNames(
            'relative flex items-baseline space-x-4 transition-opacity duration-500 ease-out transform-gpu',
            {
              'opacity-100 translate-y-0': visibleItems > index,
              'opacity-0 translate-y-4': visibleItems <= index,
            }
          )}
          style={{ transitionDelay: `${index * 300}ms` }}
        >
          {history.length > 1 && (
            <div className="flex flex-col items-center h-full">
              <div className="w-3 h-3 bg-navy-800 border-[3px] border-navy-200 rounded-full shrink-0"></div>
              {index < history.length - 1 && (
                <div
                  className={classNames(
                    'w-0.5 bg-navy-200 transition-all duration-500 ease-out',
                    {
                      'h-full': visibleItems > index,
                      'h-0': visibleItems <= index,
                    }
                  )}
                  style={{
                    transitionDelay: `${index * 300 + 200}ms`,
                  }}
                ></div>
              )}
            </div>
          )}
          <div className="w-full mb-2.5">{renderHistoryItem(item, index)}</div>
        </div>
      ))}
    </div>
  );
};

export default History;
